import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import StorefrontMaintenanceGate from './components/storefront/StorefrontMaintenanceGate';
import StorefrontBootSplash from './components/storefront/StorefrontBootSplash';

// Lazy loaded components - Code splitting için
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerCreate = lazy(() => import('./pages/CustomerCreate'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const CustomerEdit = lazy(() => import('./pages/CustomerEdit'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const ProductCreate = lazy(() => import('./pages/ProductCreate'));
const ProductEdit = lazy(() => import('./pages/ProductEdit'));
const Quotes = lazy(() => import('./pages/Quotes'));
const QuoteDetail = lazy(() => import('./pages/QuoteDetail'));
const QuoteCreate = lazy(() => import('./pages/QuoteCreate'));
const QuoteEdit = lazy(() => import('./pages/QuoteEdit'));
const Orders = lazy(() => import('./pages/Orders'));
const Payments = lazy(() => import('./pages/Payments'));
const ActiveCarts = lazy(() => import('./pages/ActiveCarts'));
const Approvals = lazy(() => import('./pages/Approvals'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const PublicQuoteView = lazy(() => import('./pages/PublicQuoteView'));
const Profile = lazy(() => import('./pages/Profile'));
const Company = lazy(() => import('./pages/Company'));
const Reports = lazy(() => import('./pages/Reports'));
const Storefront = lazy(() => import('./pages/Storefront'));
const StorefrontMemberProfile = lazy(() => import('./pages/StorefrontMemberProfile'));
const StorefrontQuoteDetail = lazy(() => import('./pages/StorefrontQuoteDetail'));
const StorefrontProductDetail = lazy(() => import('./pages/StorefrontProductDetail'));
const StorefrontLegalPage = lazy(() => import('./pages/StorefrontLegalPage'));
const StorefrontAboutPage = lazy(() => import('./pages/StorefrontAboutPage'));
const StorefrontBlogPage = lazy(() => import('./pages/StorefrontBlogPage'));
const StorefrontCartPage = lazy(() => import('./pages/StorefrontCartPage'));
const StorefrontCheckoutPage = lazy(() => import('./pages/StorefrontCheckoutPage'));
const StorefrontPaymentResultPage = lazy(() => import('./pages/StorefrontPaymentResultPage'));
const StorefrontNotFound = lazy(() => import('./pages/StorefrontNotFound'));
const LiveSupport = lazy(() => import('./pages/LiveSupport'));
const Translations = lazy(() => import('./pages/Translations'));

// Loading fallback component
const LoadingSpinner = () => {
  if (process.env.REACT_APP_PUBLIC_SITE === 'true') {
    return <StorefrontBootSplash />;
  }
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-400" />
    </div>
  );
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return user ? children : <Navigate to="/login" />;
};

const App = () => {
  const isStorefrontBuild = process.env.REACT_APP_PUBLIC_SITE === 'true';

  if (isStorefrontBuild) {
    return (
      <div className="App">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route element={<StorefrontMaintenanceGate />}>
              <Route path="/" element={<Storefront />} />
              <Route path="/store/:companyCode" element={<Storefront />} />
              <Route path="/profile" element={<StorefrontMemberProfile />} />
              <Route path="/store/:companyCode/profile" element={<StorefrontMemberProfile />} />
              <Route path="/product/:productSlug" element={<StorefrontProductDetail />} />
              <Route path="/store/:companyCode/product/:productSlug" element={<StorefrontProductDetail />} />
              <Route path="/quotes/:id" element={<StorefrontQuoteDetail />} />
              <Route path="/store/:companyCode/quotes/:id" element={<StorefrontQuoteDetail />} />
              <Route path="/yasal/:page" element={<StorefrontLegalPage />} />
              <Route path="/store/:companyCode/yasal/:page" element={<StorefrontLegalPage />} />
              <Route path="/hakkimizda" element={<StorefrontAboutPage />} />
              <Route path="/store/:companyCode/hakkimizda" element={<StorefrontAboutPage />} />
              <Route path="/blog" element={<StorefrontBlogPage />} />
              <Route path="/blog/:slug" element={<StorefrontBlogPage />} />
              <Route path="/store/:companyCode/blog" element={<StorefrontBlogPage />} />
              <Route path="/store/:companyCode/blog/:slug" element={<StorefrontBlogPage />} />
              <Route path="/sepet" element={<StorefrontCartPage />} />
              <Route path="/store/:companyCode/sepet" element={<StorefrontCartPage />} />
              <Route path="/odeme" element={<StorefrontCheckoutPage />} />
              <Route path="/store/:companyCode/odeme" element={<StorefrontCheckoutPage />} />
              <Route path="/odeme/sonuc" element={<StorefrontPaymentResultPage />} />
              <Route path="/store/:companyCode/odeme/sonuc" element={<StorefrontPaymentResultPage />} />
            </Route>
            {/* Customer quote links shared via WhatsApp/email (temizlikstore.com) */}
            <Route path="/public/quote/:token" element={<PublicQuoteView />} />
            <Route path="/public/quote/:token/:action" element={<PublicQuoteView />} />
            {/* Do not redirect unknown URLs to home — soft-404 / redirect SEO issue */}
            <Route path="*" element={<StorefrontNotFound />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  return (
    <div className="App">
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          {/* Public Quote View - No authentication required */}
          <Route path="/public/quote/:token" element={<PublicQuoteView />} />
          <Route path="/public/quote/:token/:action" element={<PublicQuoteView />} />
          <Route path="/store/:companyCode" element={<StorefrontMaintenanceGate />}>
            <Route index element={<Storefront />} />
          </Route>
          <Route path="/" element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/new" element={<CustomerCreate />} />
            <Route path="customers/:id/edit" element={<CustomerEdit />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="products" element={<Products />} />
            <Route path="products/new" element={<ProductCreate />} />
            <Route path="products/:id/edit" element={<ProductEdit />} />
            <Route path="products/:id" element={<ProductDetail />} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="quotes/create" element={<QuoteCreate />} />
            <Route path="quotes/:id/edit" element={<QuoteEdit />} />
            <Route path="quotes/:id" element={<QuoteDetail />} />
            <Route path="orders" element={<Orders />} />
            <Route path="payments" element={<Payments />} />
            <Route path="active-carts" element={<ActiveCarts />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="audit-logs" element={<AuditLogs />} />
            <Route path="profile" element={<Profile />} />
            <Route path="live-support" element={<LiveSupport />} />
            <Route path="company" element={<Company />} />
            <Route path="translations" element={<Translations />} />
            <Route path="reports" element={<Reports />} />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
};

export default App; 